import { computed, type ComputedRef, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useSearchStore } from '@stores/useSearchStore';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { useCurrency } from '@composables/useCurrency';
import { tuitionFeeLabels } from '@app/utils/formatTuitionRange';
import { useRollbar } from '@composables/useRollbar';
import { FilterProcessingService } from '@app/routing/FilterProcessingService';
import { PortalType } from '@studyportals/domain-client';
import { FormatCurrencyService } from '@infrastructure/currency/FormatCurrencyService.server';

export const useFilterSelectionText = (filterTypes: FilterType[]): ComputedRef<string> => {
	const { logError } = useRollbar();
	const searchStore = useSearchStore();
	const { filtersSelection, userCurrency, portalType, currencyConversionService} = storeToRefs(searchStore);
	const currency = useCurrency();
	const filtersProcessingService = FilterProcessingService.getInstance();

	const convertedTuitionFeeText = ref('');
	const isTuitionSummary = filterTypes.length === 1 && filterTypes[0] === FilterType.TUITION_FEE;

	const userCurr = computed(() => userCurrency.value || 'USD');

	const formatFilterValues = (type: FilterType): string => {
		const mapper = filtersProcessingService.mappers.find((m) => m.filterType === type);

		return filtersSelection.value
			.filter((filter) => filter.type === type)
			.flatMap((filter) => filter.values)
			.filter((value) => !mapper?.isImplicitForPortalType(value.id, portalType.value as PortalType))
			.map((value) => value.name)
			.join(', ');
	};

	const formatCurrencyRangeAsync = async (value: string): Promise<string> => {
		if (tuitionFeeLabels[value]) return tuitionFeeLabels[value];

		try {
			const [min, max] = JSON.parse(value) as [number, number];

			const convertedMin = min === -1 ? -1 : await currency.currencyConvert(min, 'EUR', userCurr.value);
			const convertedMax = max === -1 ? -1 : await currency.currencyConvert(max, 'EUR', userCurr.value);

			const minValue = convertedMin === -1 ? null : FormatCurrencyService.format(Math.round(convertedMin), userCurr.value);
			const maxValue = convertedMax === -1 ? null : FormatCurrencyService.format(Math.round(convertedMax), userCurr.value);

			if (min === 0 && maxValue) return `Less than ${maxValue}`;
			if (minValue && maxValue) return `${minValue} - ${maxValue}`;
			if (minValue) return `${minValue}+`;
			if (maxValue) return `Up to ${maxValue}`;
		} catch (error) {
			logError(`Failed to parse tuition fee range: ${value}`, error as Error);
		}

		return value;
	};

	if (isTuitionSummary) {
		watch(
			[filtersSelection, userCurrency, currencyConversionService],
			async () => {
				const tuitionFilter = filtersSelection.value.find((filter) => filter.type === FilterType.TUITION_FEE);
				const value = tuitionFilter?.values?.[0]?.id ?? '';
				if (value) {
					convertedTuitionFeeText.value = await formatCurrencyRangeAsync(value);
				} else {
					convertedTuitionFeeText.value = '';
				}
			},
			{ immediate: true }
		);
	}

	const selection = computed(() => {
		if (isTuitionSummary) return convertedTuitionFeeText.value;

		return filterTypes
			.map((type) => formatFilterValues(type))
			.filter(Boolean)
			.join(', ');
	});

	return selection;
};
